import {gql} from '@apollo/client'
import * as t from 'io-ts'
import React, {useEffect, useState} from 'react'
import {Helmet} from 'react-helmet-async'
import AutosizeInput from 'react-input-autosize'
import {Link, useNavigate} from 'react-router-dom'
import styled from 'styled-components'
import {ERROR_CODE_NON_UNIQUE} from '../../../common/types/constants'
import {Error} from '../../../components/auth/layout'
import {
  EditableTitle,
  PageActions,
  PageBody,
  PageHeader
} from '../../../components/page-styles'
import {Button} from '../../../components/library/buttons'
import {TextInput} from '../../../components/library/form'
import {Icon} from '../../../components/library/icon/icon'
import {Tag} from '../../../components/library/tag/tag'
import {Loader} from '../../../components/loader'
import {
  PermissionAction,
  PermissionResource
} from '../../../data/workbench-api/generated/types'
import {AlertDialog} from '../../../hooks/use-confirm'
import {useStateChanged} from '../../../hooks/use-state-changed'
import {useToast} from '../../../lib/toast'
import {OrganisationNode} from '../../../organisation'
import {
  IconHeading,
  Section,
  SectionContent,
  SectionInfo
} from '../../configuration/components'
import {
  GroupQuery,
  useGroupLazyQuery,
  useOrganisationQuery,
  useSaveGroupGrantsMutation
} from './generated'
import {ListRoles} from './list-roles'

export const PageHeaderContent = styled.div`
  display: flex;
  align-items: center;
  * {
    margin: 0 10px 0 0;
  }
`
const AnyEnvironment = t.null
const SpecificEnvironmentId = t.string
const EnvironmentsSelectedCodec = t.union([
  t.array(SpecificEnvironmentId),
  AnyEnvironment
])

export type EnvironmentsSelected = t.TypeOf<typeof EnvironmentsSelectedCodec>

const GrantsCodec = t.record(
  t.string,
  t.type({
    active: t.boolean,
    environments: EnvironmentsSelectedCodec
  })
)

export type Grants = t.TypeOf<typeof GrantsCodec>

const GroupCodec = t.intersection([
  t.type({
    name: t.string
  }),
  t.partial({
    id: t.string,
    description: t.union([t.string, t.null]),
    grants: t.union([GrantsCodec, t.null])
  })
])

type Group = t.TypeOf<typeof GroupCodec>

// This UI wants to group grants by role id, with an aggregation on applicable environments
// This function is getting the grant rows into this shape
const groupGrantsByRole = (
  grantNodes: {
    roleId: string
    environment?: {id: string; name: string} | null
  }[]
): Grants => {
  return grantNodes.reduce((acc: Grants, {roleId, environment}) => {
    const envValue = environment ? [environment.id] : null
    if (roleId in acc) {
      const existing = acc[roleId]
      return {
        ...acc,
        [roleId]: {
          ...existing,
          environments:
            existing.environments === null || envValue === null
              ? null // All environments
              : existing.environments.concat(envValue) // Individual environment
        }
      }
    }

    return {
      ...acc,
      [roleId]: {
        active: true,
        environments: envValue
      }
    }
  }, {})
}

type GrantRow = {
  roleId: string
  environmentId: string | null
}

const flattenGrants = (grants: Grants): GrantRow[] => {
  return Object.entries(grants)
    .filter(([, grant]) => grant.active)
    .reduce((grantRows: GrantRow[], [roleId, grant]) => {
      const rows = grant.environments ? grant.environments : [null]
      return grantRows.concat(
        rows.map(environmentId => ({
          roleId,
          environmentId
        }))
      )
    }, [])
}

export const Group = ({
  id,
  organisation
}: {
  id?: string
  organisation: OrganisationNode
}) => {
  const initialState = {
    name: '',
    description: ''
  }
  const [state, setState, changed, resetState] =
    useStateChanged<Group>(initialState)
  const [error, setError] = useState<string | undefined>()
  const navigate = useNavigate()
  const toast = useToast()
  const setInitialState = (data?: GroupQuery) => {
    if (data?.group) {
      const grants: Grants = groupGrantsByRole(data.group.grants.nodes)
      resetState({
        id: data.group.id,
        name: data.group.name,
        description: data.group.description,
        grants
      })
    } else {
      resetState()
    }
  }
  const [query, {data: groupData, loading: loadingGroups}] = useGroupLazyQuery({
    onCompleted: setInitialState // set internal state on load
  })
  const {data, loading} = useOrganisationQuery({
    variables: {id: organisation.id}
  })
  const [save, {loading: saving}] = useSaveGroupGrantsMutation({
    onCompleted: saved => {
      toast('Changes saved')
      setError(undefined)
      const savedId = saved?.setGroupGrants?.group?.id
      if (savedId) {
        resetState(s => ({
          ...s,
          id: savedId
        }))
      }
    },
    onError: e => {
      if (e.message.includes(ERROR_CODE_NON_UNIQUE)) {
        setError('Sorry a group with this name already exists.')
      } else {
        setError(e.message)
      }
    }
  })
  const environments =
    data?.organisation?.environments.nodes.filter(e => e.active) ?? []
  const roles = data?.organisation?.roles.nodes ?? []
  const validate = (
    s: Group
  ): {valid: false; error: string} | {valid: true} => {
    // Note if more rules are added here you'll need to update the Alert confirmation text, see render
    if (!s.name) {
      return {valid: false, error: 'Please name this group'}
    }
    return {valid: true}
  }

  useEffect(() => {
    if (id) {
      query({
        variables: {id, organisationId: organisation.id}
      })
    }
  }, [id, organisation.id, query])

  useEffect(() => {
    // Update the page URL after creating a new group
    if (state.id && !id) {
      navigate(`../group/${state.id}`, {replace: true})
    }
  }, [state.id, id, navigate])

  const onSave = async () => {
    const res = validate(state)
    if (res.valid) {
      const payload = {
        ...state,
        organisationId: organisation.id,
        grants: state?.grants ? flattenGrants(state.grants) : []
      }
      await save({
        variables: {groupGrants: payload}
      })
    } else {
      setError(res.error)
    }
  }

  const canEdit = organisation.hasPermission(
    PermissionResource.Group,
    PermissionAction.Edit
  )

  const isValid = validate(state).valid

  return loading ? (
    <Loader />
  ) : (
    <>
      <Helmet>
        <title>{state.name}</title>
      </Helmet>
      <PageHeader>
        <PageHeaderContent>
          <Link to={state.id ? '../../groups' : '../groups'}>
            <Icon name="back_large" />
          </Link>
          <EditableTitle>
            <AutosizeInput
              inputRef={instance => instance?.focus()}
              type="text"
              readOnly={!canEdit}
              placeholder="Untitled group"
              value={state.name}
              onChange={e => {
                setState(g => ({...g, name: e.target.value}))
              }}
            />
          </EditableTitle>
          <Tag appearance="blue">Group</Tag>
        </PageHeaderContent>
        <PageActions>
          {changed && canEdit && (
            <>
              <Button
                size="small"
                appearance="secondary"
                onClick={() => {
                  setInitialState(groupData)
                  setError(undefined)
                }}
              >
                Discard changes
              </Button>
              <Button
                size="small"
                appearance="primary"
                disabled={saving}
                onClick={onSave}
              >
                {state.id ? 'Update group' : 'Create group'}
              </Button>
            </>
          )}
        </PageActions>
      </PageHeader>
      <PageBody>
        {error ? <Error>{error}</Error> : null}
        <TextInput
          style={{marginBottom: '30px'}}
          label="Group description"
          disabled={!canEdit}
          readOnly={!canEdit}
          value={String(state.description || '')}
          onChange={description => setState(g => ({...g, description}))}
        />
        <Section>
          <header>
            <IconHeading>
              <Icon name="key" /> Roles and environments
            </IconHeading>
          </header>
          <SectionInfo>
            <p>
              Assign roles to this group. Roles can apply to specific
              environments or across all.
            </p>
          </SectionInfo>

          <SectionContent>
            <ListRoles
              loading={loading || loadingGroups}
              grants={state.grants || undefined}
              setGrants={update => {
                setState(g => ({...g, grants: update}))
              }}
              environmentNodes={environments}
              roles={roles}
              organisation={organisation}
            />
          </SectionContent>
        </Section>
      </PageBody>
      <AlertDialog
        isBlocking={changed}
        onSave={isValid ? onSave : undefined}
        saveText={state.id ? 'Update group' : 'Create group'}
        cancelText="Keep editing"
        discardText="Discard"
      >
        {isValid
          ? 'Would you like to save this group before you leave?'
          : 'If you would like to create this group you will need to give it a name.'}
      </AlertDialog>
    </>
  )
}

Group.fragments = {
  group: gql`
    fragment GroupGrants on Group {
      id
      name
      description
      grants: grantsByOrganisationIdAndGroupId {
        nodes {
          id
          roleId
          environment {
            id
            name
          }
        }
      }
    }
  `
}

gql`
  query Group($id: String!, $organisationId: String!) {
    group(id: $id, organisationId: $organisationId) {
      ...GroupGrants
    }
  }
  ${Group.fragments.group}
`

gql`
  query Organisation($id: String!) {
    organisation(id: $id) {
      roles: customRoles {
        nodes {
          id
        }
      }
      ...ListRolesOrganisation
    }
  }
  ${ListRoles.fragments.organisation}
`

gql`
  mutation SaveGroupGrants($groupGrants: GroupGrantsInput) {
    setGroupGrants(input: $groupGrants) {
      group {
        ...GroupGrants
      }
    }
  }
  ${Group.fragments.group}
`
